/**
 * DeliveryPreferencesPage — preferred window, safe-drop, alternate recipients,
 * notification channel + quiet-hour controls.
 *
 * Wired endpoint: `PUT /v1/customers/:id` (service-customer). The
 * `UpdateCustomerSchema` accepts an arbitrary
 * `preferences: Record<string, unknown>` blob — the page packs the
 * delivery-preference fields into that map and PUTs the whole thing.
 *
 * RBAC: `Customer:update` + `@OperatorScoped`. Citizens do not carry these
 * today (Phase 1c) — the page surfaces a 401/403 inline notice. The form
 * remains usable so reviewers can walk it; on success the customer-profile
 * cache is invalidated so other pages pick up the new preferences.
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCircle2, Clock, Loader2, Plus, Save, ShieldCheck, Trash2, Users } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import { updateCustomer, type CustomerResponse } from '../../lib/api/customer';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { customerProfileQueryKey, useCustomerProfile } from './useCustomerProfile';
import { PageHeader } from './PortalUI';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-nipost-green/20 focus:border-nipost-green';

type DeliveryWindow = 'MORNING' | 'AFTERNOON' | 'EVENING' | 'ANY';
type Channel = 'sms' | 'email' | 'whatsapp';

interface AlternateRecipient {
  readonly id: string;
  readonly name: string;
  readonly phone: string;
}

interface DeliveryPreferences {
  readonly preferredWindow: DeliveryWindow;
  readonly safeDropEnabled: boolean;
  readonly safeDropInstructions: string;
  readonly alternateRecipients: readonly AlternateRecipient[];
  readonly notifications: Readonly<Record<Channel, boolean>>;
  readonly quietHoursEnabled: boolean;
  readonly quietStart: string;
  readonly quietEnd: string;
}

const DEFAULT_PREFS: DeliveryPreferences = {
  preferredWindow: 'ANY',
  safeDropEnabled: false,
  safeDropInstructions: '',
  alternateRecipients: [],
  notifications: { sms: true, email: true, whatsapp: false },
  quietHoursEnabled: false,
  quietStart: '22:00',
  quietEnd: '07:00',
};

function readPrefs(blob: Record<string, unknown> | undefined): DeliveryPreferences {
  if (!blob) return DEFAULT_PREFS;
  const dp = blob['delivery'];
  if (typeof dp !== 'object' || dp === null) return DEFAULT_PREFS;
  const obj = dp as Record<string, unknown>;
  const window: DeliveryWindow = obj['preferredWindow'] === 'MORNING' || obj['preferredWindow'] === 'AFTERNOON' || obj['preferredWindow'] === 'EVENING' || obj['preferredWindow'] === 'ANY'
    ? (obj['preferredWindow'] as DeliveryWindow)
    : 'ANY';
  const recipients: readonly AlternateRecipient[] = Array.isArray(obj['alternateRecipients'])
    ? (obj['alternateRecipients'] as readonly unknown[]).flatMap((entry) => {
        if (typeof entry !== 'object' || entry === null) return [];
        const e = entry as Record<string, unknown>;
        if (typeof e['name'] !== 'string' || typeof e['phone'] !== 'string') return [];
        return [{
          id: typeof e['id'] === 'string' ? e['id'] : `r-${Math.random().toString(36).slice(2, 9)}`,
          name: e['name'],
          phone: e['phone'],
        }];
      })
    : [];
  const notifSrc = (obj['notifications'] as Record<string, unknown> | undefined) ?? {};
  return {
    preferredWindow: window,
    safeDropEnabled: typeof obj['safeDropEnabled'] === 'boolean' ? obj['safeDropEnabled'] : false,
    safeDropInstructions: typeof obj['safeDropInstructions'] === 'string' ? obj['safeDropInstructions'] : '',
    alternateRecipients: recipients,
    notifications: {
      sms: typeof notifSrc['sms'] === 'boolean' ? (notifSrc['sms'] as boolean) : true,
      email: typeof notifSrc['email'] === 'boolean' ? (notifSrc['email'] as boolean) : true,
      whatsapp: typeof notifSrc['whatsapp'] === 'boolean' ? (notifSrc['whatsapp'] as boolean) : false,
    },
    quietHoursEnabled: typeof obj['quietHoursEnabled'] === 'boolean' ? obj['quietHoursEnabled'] : false,
    quietStart: typeof obj['quietStart'] === 'string' ? obj['quietStart'] : '22:00',
    quietEnd: typeof obj['quietEnd'] === 'string' ? obj['quietEnd'] : '07:00',
  };
}

const WINDOWS: readonly { readonly value: DeliveryWindow; readonly label: string; readonly time: string }[] = [
  { value: 'MORNING', label: 'Morning', time: '8:00 AM – 12:00 PM' },
  { value: 'AFTERNOON', label: 'Afternoon', time: '12:00 PM – 4:00 PM' },
  { value: 'EVENING', label: 'Evening', time: '4:00 PM – 8:00 PM' },
  { value: 'ANY', label: 'Any time', time: 'No preference' },
];

export function DeliveryPreferencesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const profile = useCustomerProfile();
  const [prefs, setPrefs] = useState<DeliveryPreferences>(() => readPrefs(profile.data?.preferences));
  const [savedFlash, setSavedFlash] = useState<boolean>(false);

  useEffect(() => {
    if (profile.data) {
      setPrefs(readPrefs(profile.data.preferences));
    }
  }, [profile.data]);

  const mutation = useMutation<CustomerResponse, Error, DeliveryPreferences>({
    mutationFn: (next) => {
      if (!claims?.customerId) {
        throw new Error('Missing customer id in JWT.');
      }
      const merged: Record<string, unknown> = {
        ...(profile.data?.preferences ?? {}),
        delivery: next,
      };
      return updateCustomer(claims.customerId, { preferences: merged }, { bearerToken: token ?? undefined });
    },
    onSuccess: (data) => {
      if (claims?.customerId) {
        queryClient.setQueryData(customerProfileQueryKey(claims.customerId), data);
      }
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    },
  });

  function addRecipient(): void {
    if (prefs.alternateRecipients.length >= 3) return;
    setPrefs((p) => ({
      ...p,
      alternateRecipients: [
        ...p.alternateRecipients,
        { id: `r-${Date.now()}`, name: '', phone: '' },
      ],
    }));
  }
  function updateRecipient(id: string, field: 'name' | 'phone', value: string): void {
    setPrefs((p) => ({
      ...p,
      alternateRecipients: p.alternateRecipients.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    }));
  }
  function removeRecipient(id: string): void {
    setPrefs((p) => ({ ...p, alternateRecipients: p.alternateRecipients.filter((r) => r.id !== id) }));
  }

  function handleSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    mutation.mutate(prefs);
  }

  const errorMessage = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof ApiError && (mutation.error.status === 401 || mutation.error.status === 403)) {
      return 'Your session has expired. Please sign in again.';
    }
    return 'We couldn’t save your preferences. Please try again.';
  })();

  return (
    <div>
      <PageHeader title="Delivery Preferences" description="Customise how and when your packages are delivered." />

      <form onSubmit={handleSave} className="max-w-2xl mx-auto space-y-6" noValidate>
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Preferred delivery window</h2>
          </div>
          <div className="space-y-2">
            {WINDOWS.map((w) => (
              <label
                key={w.value}
                className={`flex items-center justify-between p-3.5 rounded-lg border cursor-pointer ${
                  prefs.preferredWindow === w.value ? 'border-nipost-green bg-nipost-green-light' : 'border-border hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="window"
                    value={w.value}
                    checked={prefs.preferredWindow === w.value}
                    onChange={() => setPrefs((p) => ({ ...p, preferredWindow: w.value }))}
                    className="w-4 h-4 text-nipost-green focus:ring-nipost-green"
                  />
                  <span className="text-sm font-medium text-gray-900">{w.label}</span>
                </div>
                <span className="text-xs text-muted">{w.time}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Safe-drop authorisation</h2>
          </div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-700">Allow delivery without signature to a safe location.</p>
              <p className="text-xs text-muted mt-0.5">The agent will leave the parcel at the location below if you are not home.</p>
            </div>
            <button
              type="button"
              onClick={() => setPrefs((p) => ({ ...p, safeDropEnabled: !p.safeDropEnabled }))}
              aria-pressed={prefs.safeDropEnabled}
              aria-label="Toggle safe-drop"
              className={`relative w-11 h-6 rounded-full ${prefs.safeDropEnabled ? 'bg-nipost-green' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.safeDropEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>
          {prefs.safeDropEnabled ? (
            <textarea
              value={prefs.safeDropInstructions}
              onChange={(e) => setPrefs((p) => ({ ...p, safeDropInstructions: e.target.value }))}
              placeholder="e.g. Leave with security guard at gate"
              rows={3}
              aria-label="Safe-drop instructions"
              className={`${inputClass} resize-none`}
            />
          ) : null}
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-nipost-green" />
              <h2 className="font-semibold text-gray-900">Alternate recipients</h2>
            </div>
            <span className="text-xs text-muted">{prefs.alternateRecipients.length}/3</span>
          </div>
          {prefs.alternateRecipients.length === 0 ? (
            <p className="text-sm text-muted mb-3">No alternate recipients yet.</p>
          ) : (
            <div className="space-y-3">
              {prefs.alternateRecipients.map((r) => (
                <div key={r.id} className="flex items-start gap-3 p-3 border border-border rounded-lg">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input
                      aria-label="Recipient name"
                      type="text"
                      placeholder="Full name"
                      value={r.name}
                      onChange={(e) => updateRecipient(r.id, 'name', e.target.value)}
                      className={inputClass}
                    />
                    <input
                      aria-label="Recipient phone"
                      type="tel"
                      placeholder="Phone"
                      value={r.phone}
                      onChange={(e) => updateRecipient(r.id, 'phone', e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRecipient(r.id)}
                    aria-label={`Remove ${r.name || 'recipient'}`}
                    className="p-1.5 text-muted hover:text-danger"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {prefs.alternateRecipients.length < 3 ? (
            <button
              type="button"
              onClick={addRecipient}
              className="flex items-center gap-2 mt-3 px-4 py-2 text-sm font-medium text-nipost-green border border-nipost-green rounded-lg hover:bg-nipost-green-light"
            >
              <Plus className="w-4 h-4" /> Add recipient
            </button>
          ) : null}
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Notification channels</h2>
          </div>
          <div className="space-y-3">
            {(['sms', 'email', 'whatsapp'] as const).map((ch) => (
              <label key={ch} className="flex items-center justify-between p-3 rounded-lg border border-border cursor-pointer">
                <span className="text-sm font-medium text-gray-900 capitalize">{ch === 'sms' ? 'SMS' : ch}</span>
                <input
                  type="checkbox"
                  checked={prefs.notifications[ch]}
                  onChange={() =>
                    setPrefs((p) => ({
                      ...p,
                      notifications: { ...p.notifications, [ch]: !p.notifications[ch] },
                    }))
                  }
                  aria-label={`Toggle ${ch} notifications`}
                  className="w-4 h-4 rounded border-border text-nipost-green focus:ring-nipost-green"
                />
              </label>
            ))}
          </div>

          <div className="mt-5 pt-5 border-t border-border">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Quiet hours</p>
                <p className="text-xs text-muted">Pause non-urgent notifications during these hours.</p>
              </div>
              <button
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, quietHoursEnabled: !p.quietHoursEnabled }))}
                aria-pressed={prefs.quietHoursEnabled}
                aria-label="Toggle quiet hours"
                className={`relative w-11 h-6 rounded-full ${prefs.quietHoursEnabled ? 'bg-nipost-green' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${prefs.quietHoursEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>
            {prefs.quietHoursEnabled ? (
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label htmlFor="dp-quiet-start" className="block text-xs font-medium text-gray-600 mb-1">Start</label>
                  <input id="dp-quiet-start" type="time" value={prefs.quietStart} onChange={(e) => setPrefs((p) => ({ ...p, quietStart: e.target.value }))} className={inputClass} />
                </div>
                <span className="text-sm text-muted mt-5">to</span>
                <div className="flex-1">
                  <label htmlFor="dp-quiet-end" className="block text-xs font-medium text-gray-600 mb-1">End</label>
                  <input id="dp-quiet-end" type="time" value={prefs.quietEnd} onChange={(e) => setPrefs((p) => ({ ...p, quietEnd: e.target.value }))} className={inputClass} />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700" role="alert">
            {errorMessage}
          </div>
        ) : null}

        {savedFlash ? (
          <div className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2" role="status">
            <CheckCircle2 className="w-3.5 h-3.5" /> Preferences saved.
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark transition-colors disabled:opacity-60"
          >
            {mutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save preferences
          </button>
        </div>
      </form>
    </div>
  );
}
